import { ExamCenterProctorType } from '@exam-center/graphql';
import { PROCTOR_TYPE_TEXTS } from '@exam-center/proctor-management';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Collapse,
  Divider,
  ExpandMoreRoundedIcon,
  FilterListOutlinedIcon,
  Flex,
  Form,
  FormSearchMultipleSelection,
  Rotate,
  SearchInput,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useExamCenterProctorListSearchParams } from '../../hooks';

const PROCTOR_TYPE_OPTIONS = Object.values(ExamCenterProctorType).map((type) => ({
  label: PROCTOR_TYPE_TEXTS[type],
  value: type,
}));

const ExamCenterProctorListFilterSchema = z.object({
  type: z
    .object({
      label: z.string(),
      value: z.nativeEnum(ExamCenterProctorType),
    })
    .array(),
});

type ExamCenterProctorListFilterFormInput = z.infer<typeof ExamCenterProctorListFilterSchema>;

export default function ExamCenterProctorListFilterSection() {
  const [{ search, proctorTypes }, { changeSearch, changeProctorTypes }] =
    useExamCenterProctorListSearchParams();
  const [isExpand, { toggle }] = useDisclosure();

  const formContext = useForm<ExamCenterProctorListFilterFormInput>({
    resolver: zodResolver(ExamCenterProctorListFilterSchema),
    defaultValues: {
      type: PROCTOR_TYPE_OPTIONS.filter((option) => proctorTypes.includes(option.value)),
    },
  });
  const { type } = formContext.watch();

  const handleReset = () => {
    formContext.setValue('type', []);
    changeProctorTypes([]);
  };

  const handleSubmit = (data: ExamCenterProctorListFilterFormInput) => {
    const { type } = data;

    const typeValues = type.map((t) => t.value);
    changeProctorTypes(typeValues);
  };

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(4),
      })}
    >
      <Flex sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ width: 300 }}>
          <SearchInput
            placeholder='ค้นหาชื่อผู้คุมสอบ...'
            defaultValue={search}
            onChange={changeSearch}
          />
        </Box>
        <Button
          variant='outlined'
          color='secondary'
          startIcon={<FilterListOutlinedIcon />}
          endIcon={
            <Rotate in={isExpand}>
              <ExpandMoreRoundedIcon />
            </Rotate>
          }
          onClick={toggle}
        >
          Filter
        </Button>
      </Flex>
      <Collapse in={isExpand} unmountOnExit>
        <FormProvider {...formContext}>
          <Form onSubmit={formContext.handleSubmit(handleSubmit)}>
            <Flex
              sx={(theme) => ({
                p: theme.spacing(4),
                borderRadius: theme.shape.rounded(2),
                border: '1px solid',
                borderColor: theme.palette.grey[300],
                flexDirection: 'column',
                gap: theme.spacing(4),
              })}
            >
              <FormSearchMultipleSelection
                name='type'
                label='ประเภทผู้คุมสอบ'
                placeholder={type.length === 0 ? 'แสดงทั้งหมด' : undefined}
                limitTags={-1}
                options={PROCTOR_TYPE_OPTIONS}
                sx={{
                  maxWidth: '260px',
                }}
              />
              <Divider />
              <Flex sx={{ gap: (theme) => theme.spacing(2), justifyContent: 'end' }}>
                <Button
                  color='secondary'
                  variant='outlined'
                  onClick={handleReset}
                  sx={{ width: 100 }}
                >
                  Reset
                </Button>
                <Button type='submit' sx={{ width: 100 }}>
                  Apply
                </Button>
              </Flex>
            </Flex>
          </Form>
        </FormProvider>
      </Collapse>
    </Flex>
  );
}
